import React, { useState } from 'react';
import {
  Button,
  Input,
  VStack,
  Select,
  createListCollection,
} from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { toaster } from '@/components/ui/toaster';
import { useWidgetPortalProps } from '@/features/widget/hooks/widget-modal-hooks';
import { createOrganization } from '@/features/organizations/organizations.service';
import {
  CreateOrganizationBody,
  Organization,
} from '@/features/organizations/organizations.types';

// Language collection
const languageCollection = createListCollection({
  items: [
    { value: '', label: 'Select Language (Optional)', code: '' },
    { value: 'en', label: 'English', code: 'en' },
    { value: 'nl', label: 'Dutch', code: 'nl' },
    { value: 'de', label: 'German', code: 'de' },
    { value: 'fr', label: 'French', code: 'fr' },
    { value: 'es', label: 'Spanish', code: 'es' },
    { value: 'it', label: 'Italian', code: 'it' },
    { value: 'pt', label: 'Portuguese', code: 'pt' },
    { value: 'ru', label: 'Russian', code: 'ru' },
    { value: 'zh', label: 'Chinese', code: 'zh' },
    { value: 'ja', label: 'Japanese', code: 'ja' },
  ],
  itemToString: (item) => item.label,
  itemToValue: (item) => item.value,
});

interface OrganisationCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newOrganisation: Organization) => void;
}

const initialFormData: Omit<CreateOrganizationBody, 'status'> = {
  name: '',
  description: '',
  website: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postal_code: '',
  country_code: '',
  language_code: '',
  pay_per_use: false,
  max_interaction_per_month: 0,
  max_interaction_per_session: 0,
};

type CreateOrganizationErrors = Partial<
  Record<keyof CreateOrganizationBody, string>
>;

const OrganisationCreateModal: React.FC<OrganisationCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] =
    useState<Omit<CreateOrganizationBody, 'status'>>(initialFormData);
  const [errors, setErrors] = useState<CreateOrganizationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const portalProps = useWidgetPortalProps();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when field is edited
    if (errors[name as keyof CreateOrganizationBody]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: CreateOrganizationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organisation name is required';
    }

    // Add more validation as needed
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Filter out empty string values
      const dataToSubmit = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          if (value !== '') {
            acc[key as keyof CreateOrganizationBody] = value as never;
          }
          return acc;
        },
        {} as Partial<CreateOrganizationBody>,
      );

      const result = await createOrganization(
        dataToSubmit as unknown as CreateOrganizationBody,
      );

      toaster.create({
        title: 'Organisation created',
        description: `${formData.name} has been successfully created`,
        type: 'success',
        duration: 5000,
      });

      // Reset form
      setFormData(initialFormData);

      // Close modal and notify parent
      onClose();
      onSuccess(result.data);
    } catch (error) {
      console.error('Error creating organisation:', error);
      toaster.create({
        title: 'Error',
        description:
          (error as Error)?.message || 'Failed to create organisation',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={() => onClose()}
      size="xl"
      closeOnInteractOutside={false}
      {...portalProps}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Organisation</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <VStack gap={4} align="stretch">
            <Field
              label="Organisation Name"
              required
              invalid={!!errors.name}
              errorText={errors.name}
            >
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter organisation name"
              />
            </Field>

            <Field label="Description">
              <Input
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description"
              />
            </Field>

            <Field
              label="Email"
              invalid={!!errors.email}
              errorText={errors.email}
            >
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
              />
            </Field>

            <Field label="Phone">
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </Field>

            <Field label="Website">
              <Input
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="Enter website URL"
              />
            </Field>

            <Field label="Address">
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter address"
              />
            </Field>

            <Field label="City">
              <Input
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
              />
            </Field>

            <Field label="Postal Code">
              <Input
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="Enter postal code"
              />
            </Field>

            <Field label="Country Code">
              <Input
                name="country_code"
                value={formData.country_code}
                onChange={handleChange}
                placeholder="Enter country code (e.g., US, UK)"
              />
            </Field>

            <Field label="Language">
              <Select.Root
                collection={languageCollection}
                size="sm"
                width="100%"
                positioning={{ sameWidth: true, strategy: 'fixed' }}
                value={formData.language_code ? [formData.language_code] : []}
                onValueChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    language_code: e.value[0] || '',
                  }));
                }}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select language" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    {languageCollection.items.map((lang) => (
                      <Select.Item key={lang.value} item={lang}>
                        {lang.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Field>

            <Field label="Pay Per Use">
              <Switch
                name="pay_per_use"
                checked={formData.pay_per_use}
                onCheckedChange={(details) => {
                  setFormData((prev) => ({
                    ...prev,
                    pay_per_use: details.checked,
                  }));
                }}
              />
            </Field>

            <Field label="Max Interactions Per Month">
              <Input
                name="max_interaction_per_month"
                type="number"
                value={formData.max_interaction_per_month}
                onChange={handleChange}
                placeholder="Enter maximum monthly interactions"
              />
            </Field>

            <Field label="Max Interactions Per Session">
              <Input
                name="max_interaction_per_session"
                type="number"
                value={formData.max_interaction_per_session}
                onChange={handleChange}
                placeholder="Enter maximum session interactions"
              />
            </Field>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <Button mr={3} onClick={handleClose} variant="outline">
            Cancel
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default OrganisationCreateModal;
